import { useCurrentUserWorkspaceRoles } from '@/generated/hooks/agora/v1/rbac';
import { useListSupportAccessGrants } from '@/generated/hooks/agora/v1/support';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { FilterBuilder } from '@/shared/utils';
import { formatTimestamp } from '@/shared/utils/datetime-utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  cn,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { useGateValue } from '@statsig/react-bindings';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const SUPPORT_ROLE_NAME = 'support-user';

const createActiveGrantsFilter = (workspaceId: string, automationId?: string) =>
  FilterBuilder.create()
    .equals('state', 'active')
    .or(
      FilterBuilder.create().equals('scope', 'organization'),

      FilterBuilder.create()
        .equals('scope', 'workspace')
        .and(FilterBuilder.create().equals('workspace_id', workspaceId)),

      ...(automationId
        ? [
            FilterBuilder.create()
              .equals('scope', 'automation')
              .and(
                FilterBuilder.create().equals('automation_id', automationId),
              ),
          ]
        : []),
    )
    .build();

interface ISupportAccessAlertProps {
  /** Optional class applied to the outer wrapper. Useful for adding gutter padding around the alert. */
  className?: string;
  automationId?: string;
}

const useIsSupportUser = (
  organizationId: string,
  workspaceId: string,
  enabled: boolean,
) => {
  const { data } = useCurrentUserWorkspaceRoles(organizationId, workspaceId, {
    query: { enabled: enabled && !!organizationId && !!workspaceId },
  });

  return (data?.data?.roles ?? []).some(
    role => role.name?.toLowerCase() === SUPPORT_ROLE_NAME,
  );
};

const SupportSessionAlert = ({ className }: ISupportAccessAlertProps) => {
  const { t } = useTranslation('settings');

  return (
    <div className={cn('w-full', className)}>
      <Alert className="flex items-start gap-3 shadow-[0_0_2px_var(--primary)]">
        <Icon type="Info" className="!text-success shrink-0" />
        <Flex direction="column" gap={1} className="flex-1 min-w-0 grow">
          <AlertTitle>
            {t('settings.supportAccess.alert.support.title')}
          </AlertTitle>
          <AlertDescription>
            <p>{t('settings.supportAccess.alert.support.description')}</p>
          </AlertDescription>
        </Flex>
      </Alert>
    </div>
  );
};

export const SupportAccessAlert = ({
  className,
  automationId,
}: ISupportAccessAlertProps) => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const isSupportAccessEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_SUPPORT_ACCESS,
  );

  const isSupportUser = useIsSupportUser(
    orgId,
    workspaceId,
    isSupportAccessEnabled,
  );

  const { data } = useListSupportAccessGrants(
    orgId,
    {
      page_size: 1,
      filter: createActiveGrantsFilter(workspaceId, automationId),
    },
    {
      query: { enabled: !!orgId && isSupportAccessEnabled && !isSupportUser },
    },
  );

  if (!isSupportAccessEnabled || !orgId) {
    return null;
  }

  if (isSupportUser) {
    return <SupportSessionAlert className={className} />;
  }

  const grants = data?.data?.support_access_grants ?? [];
  if (grants.length < 1) {
    return null;
  }

  const expiresAt = formatTimestamp(
    grants[0].expire_time ?? '',
    'MMM D [at] h:mm A',
  );

  const handleManageAccess = () => {
    navigate(getRoutePath('ORG_SETTINGS_SUPPORT_ACCESS', { orgId }));
  };

  return (
    <div className={cn('w-full', className)}>
      <Alert className="flex items-start gap-3 shadow-[0_0_2px_var(--primary)]">
        <Icon type="Info" className="!text-success shrink-0" />
        <Flex direction="column" gap={1} className="flex-1 min-w-0 grow">
          <AlertTitle>{t('settings.supportAccess.alert.title')}</AlertTitle>
          <AlertDescription>
            <p>
              <Trans
                i18nKey="settings.supportAccess.alert.description"
                ns="settings"
                values={{ expiresAt }}
                components={{ strong: <strong /> }}
              />
            </p>
          </AlertDescription>
        </Flex>

        <Button
          variant="outline"
          size="sm"
          onClick={handleManageAccess}
          data-testid="support-access-alert-manage"
          className="self-center"
        >
          {t('settings.supportAccess.alert.manageAccess')}
        </Button>
      </Alert>
    </div>
  );
};
